import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  clientesAPI,
  facturacionAPI,
  gruposAPI,
  localidadesAPI,
  provinciasAPI,
  tiposClientesAPI,
  usuariosAPI,
} from '../services/api';
import { toast } from 'react-toastify';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { ListadoScrollLayout } from '../components/ListadoScrollLayout';
import { formatFechaSistema } from '../utils/fechasSistema';
import * as listado from '../utils/facturacionListado.mjs';
import '../Clientes.css';
import './FacturacionClientes.css';

const COLUMNAS_DEFINICION = [
  { id: 'cuit', label: 'CUIT' },
  { id: 'razon_social', label: 'Razón Social' },
  { id: 'estado', label: 'Estado' },
  { id: 'tipo', label: 'Tipo' },
  { id: 'grupo', label: 'Grupo' },
  { id: 'abono_mensual', label: 'Abono mensual' },
  { id: 'empleados', label: 'Empleados' },
  { id: 'direccion', label: 'Dirección' },
  { id: 'provincia', label: 'Provincia' },
  { id: 'localidad', label: 'Localidad' },
  { id: 'telefono', label: 'Teléfono' },
  { id: 'condicion_impositiva', label: 'Cond. impositiva' },
  { id: 'ingresos_brutos', label: 'Ing. brutos' },
  { id: 'drg', label: 'DRG' },
  { id: 'dv', label: 'DV' },
  { id: 'iva_liquida', label: 'IVA líquida' },
  { id: 'saldo', label: 'Saldo' },
  { id: 'listo', label: 'Listo para facturar' },
  { id: 'faltantes', label: 'Faltantes' },
  { id: 'movimientos', label: 'Movimientos' },
  { id: 'monto', label: 'Monto a facturar' },
];

const defaultColumnasVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultOrdenColumnas = COLUMNAS_DEFINICION.map((c) => c.id);
function hoyIso() {
  const hoy = new Date();
  const m = String(hoy.getMonth() + 1).padStart(2, '0');
  const dia = String(hoy.getDate()).padStart(2, '0');
  return `${hoy.getFullYear()}-${m}-${dia}`;
}

const RECEPTOR_CLASE_A = new Set(['RESPONSABLE_INSCRIPTO', 'MONOTRIBUTO', 'MONOTRIBUTO_SOCIAL']);

function tipoComprobante(condicionEmisor, condicionReceptor) {
  if (condicionEmisor === 'monotributista') return 'FACTURA_C';
  if (RECEPTOR_CLASE_A.has(condicionReceptor)) return 'FACTURA_A';
  return 'FACTURA_B';
}

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function sortValue(cliente, columnId) {
  switch (columnId) {
    case 'cuit': return (cliente.cuit || '').toString().toLowerCase();
    case 'razon_social': return (cliente.razon_social || '').toString().toLowerCase();
    case 'estado': return cliente.activo ? 1 : 0;
    case 'tipo': return (cliente.tipo_nombre || '').toString().toLowerCase();
    case 'grupo': return (cliente.grupo_nombre || '').toString().toLowerCase();
    case 'abono_mensual': return parseFloat(cliente.abono_mensual) || 0;
    case 'empleados': return parseInt(cliente.cantidad_empleados, 10) || 0;
    case 'direccion': return (cliente.direccion || '').toString().toLowerCase();
    case 'provincia': return (cliente.provincia_nombre || '').toString().toLowerCase();
    case 'localidad': return (cliente.localidad_nombre || '').toString().toLowerCase();
    case 'telefono': return (cliente.telefono || '').toString().toLowerCase();
    case 'condicion_impositiva': return (cliente.condicion_impositiva || '').toString().toLowerCase();
    case 'ingresos_brutos': return (cliente.ingresos_brutos || '').toString().toLowerCase();
    case 'drg': return (cliente.drg || '').toString().toLowerCase();
    case 'dv': return (cliente.dv || '').toString().toLowerCase();
    case 'iva_liquida': return parseInt(cliente.iva_liquida, 10) || 0;
    case 'saldo': return parseFloat(cliente.saldo) || 0;
    case 'listo': return cliente.listo ? 1 : 0;
    case 'faltantes': return (cliente.faltantes || []).join(', ').toLowerCase();
    case 'movimientos': return cliente.cantidad || 0;
    case 'monto': return cliente.monto || 0;
    default: return '';
  }
}

const FacturacionClientes = () => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [modo, setModo] = useState(listado.leerModoGuardado);
  const [fecha, setFecha] = useState(hoyIso);
  const [conceptoDetalle, setConceptoDetalle] = useState('');
  const [candidatos, setCandidatos] = useState([]);
  const [clientesApi, setClientesApi] = useState([]);
  const [seleccion, setSeleccion] = useState({});
  const [soloSeleccionadas, setSoloSeleccionadas] = useState(false);
  const [expandidos, setExpandidos] = useState({});
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [filtrosValores, setFiltrosValores] = useState(listado.leerFiltrosGuardados);
  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisibles }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisibles }));
  const [ordenColumnas, setOrdenColumnas] = useState(() => [...defaultOrdenColumnas]);
  const [sortByColumn, setSortByColumn] = useState('razon_social');
  const [sortAsc, setSortAsc] = useState(true);

  const filtroVisible = useCallback(
    (colId) => filtrosVisibles[colId] !== false && columnasVisibles[colId] !== false,
    [filtrosVisibles, columnasVisibles]
  );
  const setFiltro = (key, value) => setFiltrosValores((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    listado.guardarFiltrosSesion(filtrosValores);
  }, [filtrosValores]);

  useEffect(() => {
    listado.guardarModoSesion(modo);
  }, [modo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await usuariosAPI.getPreferencias(listado.clavePreferencias());
        if (cancelled) return;
        const raw = res.data;
        const data = raw?.valor
          ?? (raw && typeof raw === 'object' && raw.columnasVisibles != null ? raw : null);
        if (!data || typeof data !== 'object') return;
        if (data.columnasVisibles) setColumnasVisibles({ ...defaultColumnasVisibles, ...data.columnasVisibles });
        if (data.filtrosVisibles) setFiltrosVisibles({ ...defaultFiltrosVisibles, ...data.filtrosVisibles });
        if (Array.isArray(data.ordenColumnas) && data.ordenColumnas.length) {
          const known = new Set(COLUMNAS_DEFINICION.map((c) => c.id));
          const saved = data.ordenColumnas.filter((id) => known.has(id));
          const missing = defaultOrdenColumnas.filter((id) => !saved.includes(id));
          setOrdenColumnas([...saved, ...missing]);
        }
      } catch (e) { /* sin preferencias */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const guardarPreferencias = useCallback(async (snapshot) => {
    const payload = {
      columnasVisibles: snapshot?.columnasVisibles ? { ...snapshot.columnasVisibles } : { ...columnasVisibles },
      filtrosVisibles: snapshot?.filtrosVisibles ? { ...snapshot.filtrosVisibles } : { ...filtrosVisibles },
      ordenColumnas: snapshot?.ordenColumnas ? [...snapshot.ordenColumnas] : [...ordenColumnas],
    };
    if (snapshot?.columnasVisibles) setColumnasVisibles(payload.columnasVisibles);
    if (snapshot?.filtrosVisibles) setFiltrosVisibles(payload.filtrosVisibles);
    if (snapshot?.ordenColumnas) setOrdenColumnas(payload.ordenColumnas);
    try {
      await usuariosAPI.savePreferencias(listado.clavePreferencias(), payload);
      toast.success('Preferencias de columnas y filtros guardadas.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar preferencias');
    }
  }, [columnasVisibles, filtrosVisibles, ordenColumnas]);

  const cargarMeta = useCallback(async () => {
    const res = await facturacionAPI.meta();
    if (res.success) setMeta(res.data);
  }, []);

  const cargarCandidatos = useCallback(async () => {
    const res = await facturacionAPI.candidatos({ modo });
    if (!res.success) throw new Error(res.message || 'No se pudieron cargar los movimientos');
    setCandidatos(res.data || []);
  }, [modo]);

  const cargarClientes = useCallback(async () => {
    const res = await clientesAPI.getAll(listado.paramsConsultaClientes(filtrosValores, filtroVisible));
    if (!res.success) throw new Error(res.message || 'No se pudieron cargar los clientes');
    setClientesApi(res.data || []);
  }, [filtrosValores, filtroVisible]);

  useEffect(() => {
    cargarMeta().catch((error) => toast.error(error.message));
    tiposClientesAPI.getAll().then((r) => r.success && setTipos(r.data || [])).catch(() => {});
    gruposAPI.getAll().then((r) => r.success && setGrupos(r.data || [])).catch(() => {});
    provinciasAPI.getAll().then((r) => r.success && setProvincias(r.data || [])).catch(() => {});
  }, [cargarMeta]);

  useEffect(() => {
    if (!filtrosValores.provincia_id) {
      setLocalidades([]);
      return;
    }
    localidadesAPI.getByProvincia(filtrosValores.provincia_id)
      .then((r) => setLocalidades(r.success ? r.data || [] : []))
      .catch(() => setLocalidades([]));
  }, [filtrosValores.provincia_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCargando(true);
      try {
        await Promise.all([cargarCandidatos(), cargarClientes()]);
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'No se pudo armar el listado');
      } finally {
        if (!cancelled) setCargando(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cargarCandidatos, cargarClientes]);

  const filas = useMemo(
    () => listado.filasDeCandidatos(clientesApi, candidatos, filtrosValores, filtroVisible),
    [candidatos, clientesApi, filtroVisible, filtrosValores]
  );

  const ordenadas = useMemo(() => {
    const lista = [...filas];
    if (!sortByColumn) return lista;
    lista.sort((a, b) => {
      const va = sortValue(a, sortByColumn);
      const vb = sortValue(b, sortByColumn);
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
    return lista;
  }, [filas, sortByColumn, sortAsc]);

  const lista = useMemo(
    () => (soloSeleccionadas ? listado.filtrarSoloSeleccionadas(ordenadas, seleccion) : ordenadas),
    [ordenadas, seleccion, soloSeleccionadas]
  );

  const lote = useMemo(() => listado.resumenSeleccion(ordenadas, seleccion), [ordenadas, seleccion]);

  const ordenColumnasVisible = useMemo(
    () => ordenColumnas.filter((id) => columnasVisibles[id] !== false),
    [ordenColumnas, columnasVisibles]
  );

  const tiposEnJuego = useMemo(() => {
    const tiposSet = new Set();
    const condicionEmisor = meta?.emisor?.condicionEmisor;
    if (!condicionEmisor) return tiposSet;
    for (const cliente of ordenadas) {
      const ids = seleccion[cliente.clave] || [];
      if (!ids.length || !cliente.listo) continue;
      tiposSet.add(tipoComprobante(condicionEmisor, cliente.condicion));
    }
    return tiposSet;
  }, [ordenadas, seleccion, meta]);

  const fechaMinima = useMemo(
    () => listado.fechaMinimaEmision(tiposEnJuego, meta?.ultimasFechas),
    [tiposEnJuego, meta]
  );

  const fechaInvalida = Boolean(fechaMinima && fecha < fechaMinima);

  const resumenArca = useMemo(() => listado.armarResumenArca(meta), [meta]);

  const toggleCliente = (cliente) => {
    if (!cliente.listo) return;
    const todos = cliente.movimientos.map((m) => m.id);
    const actual = seleccion[cliente.clave] || [];
    const completo = todos.length > 0 && todos.every((id) => actual.includes(id));
    setSeleccion((prev) => ({ ...prev, [cliente.clave]: completo ? [] : todos }));
    if (!completo) setExpandidos((prev) => ({ ...prev, [cliente.clave]: true }));
  };

  const toggleMovimiento = (cliente, movimientoId) => {
    if (!cliente.listo) return;
    setSeleccion((prev) => {
      const actual = new Set(prev[cliente.clave] || []);
      if (actual.has(movimientoId)) actual.delete(movimientoId);
      else actual.add(movimientoId);
      return { ...prev, [cliente.clave]: [...actual] };
    });
  };

  const seleccionarListos = () => {
    const next = {};
    ordenadas.filter((c) => c.listo).forEach((c) => {
      next[c.clave] = c.movimientos.map((m) => m.id);
    });
    setSeleccion(next);
  };

  const handleSort = (colId) => {
    if (sortByColumn === colId) setSortAsc((v) => !v);
    else {
      setSortByColumn(colId);
      setSortAsc(true);
    }
  };

  const emitir = async () => {
    const pedido = listado.armarPedidoEmision(ordenadas, seleccion);
    if (!pedido.clientes.length && !pedido.grupos.length) {
      toast.error('Elegí movimientos de clientes con los datos fiscales completos');
      return;
    }
    if (fechaInvalida) {
      toast.error(`La fecha no puede ser anterior a ${fechaMinima}`);
      return;
    }
    if (modo === 'recibo' && !conceptoDetalle.trim()) {
      toast.error('Indicá el concepto de la factura');
      return;
    }
    setEmitiendo(true);
    try {
      const res = await facturacionAPI.emitir({
        modo,
        fecha,
        conceptoDetalle: modo === 'recibo' ? conceptoDetalle.trim() : '',
        clientes: pedido.clientes,
        grupos: pedido.grupos,
      });
      if (!res.success) {
        toast.error(res.message || 'No se pudo emitir');
        return;
      }
      const filas = res.data || [];
      setResultados(filas);
      setSeleccion({});
      const aprobadas = filas.filter((fila) => fila.estado === 'aprobada');
      const fallidas = filas.filter((fila) => fila.estado !== 'aprobada');
      const textoUno = fallidas[0]
        ? `${fallidas[0].razonSocial || 'Cliente'}: ${fallidas[0].mensaje || fallidas[0].estado}`
        : '';
      if (aprobadas.length && !fallidas.length) {
        toast.success(aprobadas.length === 1 ? '1 factura aprobada' : `${aprobadas.length} facturas aprobadas`);
      } else if (fallidas.length === 1 && !aprobadas.length) {
        toast.error(textoUno, { autoClose: false });
      } else if (!aprobadas.length) {
        toast.error(`${fallidas.length} facturas con error. El detalle está en el log.`, { autoClose: false });
      } else {
        toast.warning(`${aprobadas.length} aprobadas, ${fallidas.length} con error. El detalle está en el log.`, { autoClose: false });
      }
      await Promise.all([cargarCandidatos(), cargarMeta()]);
    } catch (error) {
      toast.error(error.message || 'No se pudo emitir');
    } finally {
      setEmitiendo(false);
    }
  };

  const guardarLog = () => {
    const celda = (valor) => `"${String(valor ?? '').replace(/"/g, '""')}"`;
    const encabezado = ['cliente', 'estado', 'comprobante', 'numero', 'cae', 'mensaje'];
    const lineas = resultados.map((fila) => [
      fila.razonSocial || `Cliente ${fila.clienteId}`,
      fila.estado,
      fila.tipoComprobante || '',
      fila.numero || '',
      fila.cae || '',
      fila.mensaje || '',
    ].map(celda).join(';'));
    const blob = new Blob([`\uFEFF${encabezado.join(';')}\n${lineas.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    const marca = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    enlace.href = url;
    enlace.download = `facturacion-${marca}.csv`;
    enlace.click();
    URL.revokeObjectURL(url);
  };

  const abrirEmitidas = () => {
    navigate('/clientes/facturacion/emitidas', {
      state: {
        clienteIds: listado.idsClientesEmitidas({
          clientesApi,
          filas,
          filtrosVisibles,
          columnasVisibles,
          filtrosValores,
        }),
      },
    });
  };

  const celda = (cliente, colId) => {
    if (colId === 'cuit') return <strong>{cliente.cuit}</strong>;
    if (colId === 'razon_social') {
      if (!cliente.grupoFactura) return cliente.razon_social;
      return (
        <>
          <span className="facturacion-grupo-tag">Grupo {cliente.grupoFactura.nombre}</span>
          {cliente.razon_social}
        </>
      );
    }
    if (colId === 'estado') return cliente.activo ? 'Activo' : 'Inactivo';
    if (colId === 'tipo') return cliente.tipo_nombre || '-';
    if (colId === 'grupo') return cliente.grupo_nombre || '-';
    if (colId === 'abono_mensual') return formatMoneda(cliente.abono_mensual);
    if (colId === 'empleados') return cliente.cantidad_empleados ?? '-';
    if (colId === 'direccion') return cliente.direccion || '-';
    if (colId === 'provincia') return cliente.provincia_nombre || '-';
    if (colId === 'localidad') return cliente.localidad_nombre || '-';
    if (colId === 'telefono') return cliente.telefono || '-';
    if (colId === 'condicion_impositiva') return cliente.condicion_impositiva || '-';
    if (colId === 'ingresos_brutos') return cliente.ingresos_brutos || '-';
    if (colId === 'drg') return cliente.drg || '-';
    if (colId === 'dv') return cliente.dv || '-';
    if (colId === 'iva_liquida') {
      if (cliente.iva_liquida === 1 || cliente.iva_liquida === '1') return 'Sí';
      if (cliente.iva_liquida === 0 || cliente.iva_liquida === '0') return 'No';
      return '-';
    }
    if (colId === 'saldo') return formatMoneda(cliente.saldo);
    if (colId === 'listo') return cliente.listo ? 'Sí' : 'No';
    if (colId === 'faltantes') return cliente.faltantes.length ? cliente.faltantes.join(', ') : '-';
    if (colId === 'movimientos') return cliente.cantidad;
    if (colId === 'monto') return formatMoneda(cliente.monto);
    return '';
  };

  const labelCol = (id) => COLUMNAS_DEFINICION.find((c) => c.id === id)?.label || id;
  const listosVisibles = lista.filter((c) => c.listo);
  const todosMarcados = listosVisibles.length > 0 && listosVisibles.every((c) => {
    const ids = seleccion[c.clave] || [];
    return c.movimientos.every((m) => ids.includes(m.id));
  });

  return (
    <ListadoScrollLayout
      className="clientes clientes-con-listado"
      header={(
        <div className="page-header">
          <h1>Facturación</h1>
        </div>
      )}
      fixedTop={(
        <div className="facturacion-fijo">
          {meta && !meta.emisor.listo && (
            <div className="facturacion-error">
              Antes de emitir faltan datos del emisor: {meta.emisor.faltantes.join(', ')}. Cargalos en Configuración. Entorno: {meta.entorno}.
            </div>
          )}
          <div className="filters">
            <select value={modo} onChange={(e) => setModo(e.target.value)} title="Qué facturar">
              <option value="recibo">Ya cobrado (con recibo)</option>
              <option value="debito">Para cobrar (débitos)</option>
            </select>
            <label className="facturacion-fecha-label">Fecha de factura
              <input type="date" value={fecha} min={fechaMinima || undefined} onChange={(e) => setFecha(e.target.value)} />
            </label>
            {modo === 'recibo' && (
              <label className="facturacion-fecha-label">Concepto de la factura
                <input
                  type="text"
                  value={conceptoDetalle}
                  maxLength={255}
                  placeholder="Ej. Honorarios septiembre"
                  className="facturacion-concepto"
                  onChange={(e) => setConceptoDetalle(e.target.value)}
                />
              </label>
            )}
            <button type="button" className="btn-primary" onClick={emitir} disabled={emitiendo || !meta?.emisor?.listo || fechaInvalida}>
              {emitiendo ? 'Emitiendo…' : 'Emitir seleccionadas'}
            </button>
            <span className="facturacion-resumen-seleccion">
              {lote.cantidad === 1 ? '1 factura' : `${lote.cantidad} facturas`} · {formatMoneda(lote.importe)}
            </span>
          </div>
          {resultados.length > 0 && (
            <div className="facturacion-log">
              <div className="facturacion-log-barra">
                <span>
                  {resultados.filter((fila) => fila.estado === 'aprobada').length} aprobadas, {resultados.filter((fila) => fila.estado !== 'aprobada').length} con error
                </span>
                <span className="facturacion-log-acciones">
                  <button type="button" className="btn-secondary" onClick={guardarLog}>Guardar log</button>
                  <button type="button" className="btn-secondary" onClick={() => setResultados([])}>Descartar log</button>
                </span>
              </div>
              <ul className="facturacion-resultados">
                {resultados.map((fila) => (
                  <li key={`${fila.facturaId || (fila.grupoId ? `g${fila.grupoId}` : fila.clienteId)}-${fila.estado}`} className={fila.estado === 'aprobada' ? 'ok' : 'error'}>
                    {fila.razonSocial || `Cliente ${fila.clienteId}`}: {fila.estado === 'aprobada' ? 'aprobada' : (fila.mensaje || fila.estado)}
                    {fila.numero ? ` · Nº ${fila.numero}` : ''}
                    {fila.cae ? ` · CAE ${fila.cae}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="facturacion-aviso">
            {meta?.avisoArca
              ? `No se pudo consultar ARCA. ${meta.avisoArca}`
              : 'La fecha no puede ser anterior a la última de ese tipo en ARCA. El mismo día sí. El número lo asigna ARCA al emitir.'}
            {resumenArca.length > 0 && (
              <span>
                {' '}
                {resumenArca.map((fila) => `Factura ${fila.letra}: última ${fila.fecha ? formatFechaSistema(fila.fecha) : 'sin comprobantes'}, próximo ${fila.numero}`).join(' · ')}
              </span>
            )}
          </div>
        </div>
      )}
      filters={(
        <div className="filters">
          {filtroVisible('cuit') && (
            <input type="text" placeholder="CUIT" value={filtrosValores.cuit} onChange={(e) => setFiltro('cuit', e.target.value)} className="search-input" style={{ maxWidth: 140 }} />
          )}
          {filtroVisible('razon_social') && (
            <>
              <input type="text" placeholder="Razón social" value={filtrosValores.razon_social} onChange={(e) => setFiltro('razon_social', e.target.value)} className="search-input" style={{ maxWidth: 180 }} />
              <label className="facturacion-check-label" title="Filtrar desde la primera letra">
                <input type="checkbox" checked={!!filtrosValores.razon_social_prefix} onChange={(e) => setFiltro('razon_social_prefix', e.target.checked)} />
                Desde 1ª letra
              </label>
            </>
          )}
          {filtroVisible('estado') && (
            <select value={filtrosValores.activo} onChange={(e) => setFiltro('activo', e.target.value)} title="Estado">
              <option value="true">Solo activos</option>
              <option value="false">Solo inactivos</option>
              <option value="">Todos</option>
            </select>
          )}
          {filtroVisible('tipo') && (
            <select value={filtrosValores.tipo_id} onChange={(e) => setFiltro('tipo_id', e.target.value)}>
              <option value="">Tipo</option>
              {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          )}
          {filtroVisible('grupo') && (
            <select value={filtrosValores.grupo_id} onChange={(e) => setFiltro('grupo_id', e.target.value)}>
              <option value="">Grupo</option>
              {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          )}
          {filtroVisible('abono_mensual') && (
            <input type="number" placeholder="Abono mín." value={filtrosValores.abono_mensual_min} onChange={(e) => setFiltro('abono_mensual_min', e.target.value)} min="0" step="0.01" style={{ width: 100 }} />
          )}
          {filtroVisible('empleados') && (
            <input type="number" placeholder="Empleados" value={filtrosValores.cantidad_empleados} onChange={(e) => setFiltro('cantidad_empleados', e.target.value)} min="0" style={{ width: 90 }} />
          )}
          {filtroVisible('direccion') && (
            <input type="text" placeholder="Dirección" value={filtrosValores.direccion} onChange={(e) => setFiltro('direccion', e.target.value)} style={{ maxWidth: 140 }} />
          )}
          {filtroVisible('provincia') && (
            <select value={filtrosValores.provincia_id} onChange={(e) => { setFiltro('provincia_id', e.target.value); setFiltro('localidad_id', ''); }}>
              <option value="">Provincia</option>
              {provincias.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          )}
          {filtroVisible('localidad') && (
            <select value={filtrosValores.localidad_id} onChange={(e) => setFiltro('localidad_id', e.target.value)} disabled={!filtrosValores.provincia_id}>
              <option value="">Localidad</option>
              {localidades.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          )}
          {filtroVisible('telefono') && (
            <input type="text" placeholder="Teléfono" value={filtrosValores.telefono} onChange={(e) => setFiltro('telefono', e.target.value)} style={{ maxWidth: 120 }} />
          )}
          {filtroVisible('condicion_impositiva') && (
            <input type="text" placeholder="Cond. impositiva" value={filtrosValores.condicion_impositiva} onChange={(e) => setFiltro('condicion_impositiva', e.target.value)} style={{ maxWidth: 140 }} />
          )}
          {filtroVisible('ingresos_brutos') && (
            <input type="text" placeholder="Ing. brutos" value={filtrosValores.ingresos_brutos} onChange={(e) => setFiltro('ingresos_brutos', e.target.value)} style={{ maxWidth: 120 }} />
          )}
          {filtroVisible('drg') && (
            <input type="text" placeholder="DRG" value={filtrosValores.drg} onChange={(e) => setFiltro('drg', e.target.value)} style={{ maxWidth: 100 }} />
          )}
          {filtroVisible('dv') && (
            <input type="text" placeholder="DV" value={filtrosValores.dv} onChange={(e) => setFiltro('dv', e.target.value)} style={{ maxWidth: 80 }} />
          )}
          {filtroVisible('iva_liquida') && (
            <select value={filtrosValores.iva_liquida} onChange={(e) => setFiltro('iva_liquida', e.target.value)}>
              <option value="">IVA líquida</option>
              <option value="1">Sí</option>
              <option value="0">No</option>
            </select>
          )}
          {filtroVisible('saldo') && (
            <>
              <input type="number" placeholder="Saldo mín." value={filtrosValores.saldo_min} onChange={(e) => setFiltro('saldo_min', e.target.value)} step="0.01" style={{ width: 95 }} />
              <input type="number" placeholder="Saldo máx." value={filtrosValores.saldo_max} onChange={(e) => setFiltro('saldo_max', e.target.value)} step="0.01" style={{ width: 95 }} />
            </>
          )}
          {filtroVisible('listo') && (
            <select value={filtrosValores.listo} onChange={(e) => setFiltro('listo', e.target.value)}>
              <option value="">Listo para facturar</option>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select>
          )}
          {filtroVisible('faltantes') && (
            <input type="text" placeholder="Faltantes" value={filtrosValores.faltantes} onChange={(e) => setFiltro('faltantes', e.target.value)} style={{ maxWidth: 140 }} />
          )}
          {filtroVisible('movimientos') && (
            <input type="number" placeholder="Movim. mín." value={filtrosValores.movimientos_min} onChange={(e) => setFiltro('movimientos_min', e.target.value)} min="0" style={{ width: 110 }} />
          )}
          {filtroVisible('monto') && (
            <input type="number" placeholder="Monto mín." value={filtrosValores.monto_min} onChange={(e) => setFiltro('monto_min', e.target.value)} min="0" step="0.01" style={{ width: 110 }} />
          )}
        </div>
      )}
      toolbar={(
        <div className="clientes-toolbar-acciones">
          <div className="clientes-toolbar-acciones-inner">
            <div className="toolbar-grupo toolbar-grupo--seleccion">
              <button type="button" className="btn-action-icon" onClick={() => setFiltrosValores(listado.filtrosIniciales())} title="Limpiar todos los filtros">
                <span aria-hidden="true">🧹</span>
              </button>
              <button type="button" className="btn-action-icon" onClick={seleccionarListos} title="Seleccionar clientes listos para facturar">
                <span aria-hidden="true">✅</span>
              </button>
              <button type="button" className="btn-action-icon" onClick={() => setSeleccion({})} title="Deseleccionar todos">
                <span aria-hidden="true">✖️</span>
              </button>
              <button
                type="button"
                className="btn-secondary facturacion-solo-seleccionadas"
                aria-pressed={soloSeleccionadas}
                onClick={() => setSoloSeleccionadas((v) => !v)}
              >
                Solo seleccionadas
              </button>
              <button type="button" className="btn-secondary" onClick={abrirEmitidas}>Emitidas</button>
            </div>
            <span className="toolbar-grupo--separador" aria-hidden="true" />
            <div className="toolbar-grupo toolbar-grupo--listado">
              <SelectorColumnas
                columnas={COLUMNAS_DEFINICION}
                visibleIds={Object.keys(columnasVisibles).filter((id) => columnasVisibles[id])}
                ordenColumnas={ordenColumnas}
                onOrdenChange={setOrdenColumnas}
                onToggle={(id) => setColumnasVisibles((prev) => ({ ...prev, [id]: !prev[id] }))}
                onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisibles })}
                onSave={guardarPreferencias}
                titulo="Columnas visibles"
                labelBoton="Columnas"
              />
              <SelectorFiltros
                filtros={COLUMNAS_DEFINICION}
                visibleIds={Object.keys(filtrosVisibles).filter((id) => filtrosVisibles[id])}
                onToggle={(id) => setFiltrosVisibles((prev) => ({ ...prev, [id]: !prev[id] }))}
                onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisibles })}
                onSave={guardarPreferencias}
                titulo="Filtros visibles"
                labelBoton="Filtros"
              />
            </div>
          </div>
        </div>
      )}
      refreshing={cargando}
    >
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: 44 }}>
              <input
                type="checkbox"
                checked={todosMarcados}
                onChange={() => (todosMarcados ? setSeleccion({}) : seleccionarListos())}
                title="Seleccionar clientes listos"
                aria-label="Seleccionar clientes listos"
              />
            </th>
            <th style={{ width: 36 }} />
            {ordenColumnasVisible.map((colId) => (
              <th key={colId} className="th-sortable" onClick={() => handleSort(colId)}>
                <span>{labelCol(colId)}</span>
                {sortByColumn === colId && <span className="sort-indicator">{sortAsc ? ' ▲' : ' ▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lista.length === 0 ? (
            <tr>
              <td colSpan={2 + ordenColumnasVisible.length} className="no-data">
                {cargando
                  ? 'Cargando…'
                  : (soloSeleccionadas
                    ? 'No hay facturas seleccionadas'
                    : 'No hay clientes con movimientos para facturar en este período')}
              </td>
            </tr>
          ) : lista.map((cliente) => {
            const marcados = seleccion[cliente.clave] || [];
            const completo = cliente.listo && cliente.movimientos.every((m) => marcados.includes(m.id));
            const abierto = !!expandidos[cliente.clave];
            const esGrupo = Boolean(cliente.grupoFactura);
            return (
              <React.Fragment key={cliente.clave}>
                <tr className="table-row">
                  <td>
                    <input
                      type="checkbox"
                      checked={completo && marcados.length > 0}
                      disabled={!cliente.listo}
                      onChange={() => toggleCliente(cliente)}
                      aria-label={`Seleccionar ${cliente.razon_social}`}
                      title={cliente.listo ? 'Seleccionar movimientos' : (cliente.faltantes || []).join(', ')}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-action-icon"
                      onClick={() => setExpandidos((prev) => ({ ...prev, [cliente.clave]: !prev[cliente.clave] }))}
                      title={abierto ? 'Ocultar movimientos' : 'Ver movimientos'}
                    >
                      {abierto ? '▾' : '▸'}
                    </button>
                  </td>
                  {ordenColumnasVisible.map((colId) => (
                    <td key={colId}>{celda(cliente, colId)}</td>
                  ))}
                </tr>
                {abierto && (
                  <tr className="facturacion-detalle">
                    <td colSpan={2 + ordenColumnasVisible.length}>
                      {esGrupo && (
                        <p>
                          Una sola factura a {cliente.razon_social} por los {cliente.grupoFactura.integrantes.length} integrantes con movimientos del grupo {cliente.grupoFactura.nombre}.
                        </p>
                      )}
                      {!cliente.listo && <div className="facturacion-error">Faltan datos: {cliente.faltantes.join(', ')}.</div>}
                      {cliente.avisos?.length > 0 && <p>Para el PDF conviene completar: {cliente.avisos.join(', ')}.</p>}
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th style={{ width: 44 }} />
                            <th>Fecha</th>
                            {esGrupo && <th>Integrante</th>}
                            <th>Concepto</th>
                            <th>Importe</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cliente.movimientos.map((mov) => (
                            <tr key={mov.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  disabled={!cliente.listo}
                                  checked={marcados.includes(mov.id)}
                                  onChange={() => toggleMovimiento(cliente, mov.id)}
                                  aria-label={mov.concepto}
                                />
                              </td>
                              <td>{formatFechaSistema(mov.fecha)}</td>
                              {esGrupo && <td>{mov.razon_social}</td>}
                              <td>{mov.concepto}</td>
                              <td>{formatMoneda(mov.monto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </ListadoScrollLayout>
  );
};

export default FacturacionClientes;
